<template>
  <div ref="monthEl" :class="get_month_class(monthObject)" :tabindex="tabindex" @focus="initTooltip"
       @mouseenter="initTooltip" @mouseleave="teardownTooltip">
    <div class="rotations_container">
      <div class="columns" v-for="rotations_group in rotations_group_of_4">
        <template v-for="rotation in rotations_group">
          <div class="column is-one-quarter has-text-white has-background-black" v-if="monthObject.active">
            {{rotation}}
          </div>
        </template>
      </div>
    </div>
    <div class="image_or_dropdown_container">
      <template v-if="monthObject.active">
        <div class="dropdown_container control" v-if="is_current_month()">
          <select class="select" @change="show_hide_neighbouring_cohorts" aria-label="show/hide cohorts for neighboring months">
            <option v-for="timeline in rotation_timeline" :value="timeline">{{timeline}}</option>
          </select>
        </div>
        <div class="image_container columns" v-else>
          <div class="column is-two-third image_icon"></div>
          <div class="column is-one-third percentage_text">{{ (monthObject.rotations.length)*100/8 }}%</div>
        </div>
      </template>
    </div>
    <month-topics :year="year" :month="month" />
  </div>
</template>


<script setup>
import {computed, inject, provide, ref, toRef, useTemplateRef} from 'vue';
import MonthTopics from "./MonthTopics.vue";
import {makeTooltipData} from "../lib/helpers";
import {useMonthTooltip} from "../composables/useMonthTooltip";

const rotation_timeline = ["Included in this month", "Started this month", "Finished this month"];

const props = defineProps({
  year: Number,
  // 0-based month
  month: Number
});

const tooltips_enabled = ref(true);
const tooltip_data = inject("tooltip_data");
const monthEl = useTemplateRef("monthEl");

// get the selected topics function
const getSelectedSampleTopics = inject("getSelectedSampleTopics");

// get the selected topics computed/ref for THIS cell
const selectedSampleTopics = getSelectedSampleTopics(props.year, props.month);

// Provide the computed/ref, not the function
provide("selectedSampleTopics", selectedSampleTopics);

const {tabindex, initTooltip, teardownTooltip} = useMonthTooltip({
  year: toRef(props, "year"),
  month: toRef(props, "month"),
  monthEl,
  tooltips_enabled,
  tooltip_data,
  makeTooltipData,
  getSelectedSampleTopics
});

const grid = inject("atusCalendarGrid");
const monthObject = computed(() => {
  const gridYear = grid.value[props.year];
  return gridYear.months[props.month];
});

const get_month_class = (month_object) => {
  if(month_object.active){
    return "round_"+month_object.rotations.length;
  }
}

const is_current_month = () => {
  return monthObject.value.rotations.length === 8;
}

const show_hide_neighbouring_cohorts = (event) => {
  var value = event.target.value;
  var show_months = [];
  var hide_months = [];

  if (value === rotation_timeline[0]) {
    for (let c = 0; c < monthObject.value.cohorts.length; c++) {
      show_cohort_months(monthObject.value.cohorts[c]);
    }
  }

  // For: "Started this month"
  if (value === rotation_timeline[1]) {
    hide_cohort_months(monthObject.value.cohorts[0]);
    for (let n = 0; n < 3; n++) {
      (monthObject.value.cohorts[1] !== null) ? hide_months.push(monthObject.value.cohorts[1][n]) : '';
      (monthObject.value.cohorts[2] !== null) ? hide_months.push(monthObject.value.cohorts[2][n]) : '';
    }
    for (let n = 3; n < 7; n++) {
      (monthObject.value.cohorts[1] !== null) ? show_months.push(monthObject.value.cohorts[1][n]) : '';
      (monthObject.value.cohorts[2] !== null) ? show_months.push(monthObject.value.cohorts[2][n]) : '';
    }
  }

  // For: "Finished this month"
  if (value === rotation_timeline[2]) {
    for (let n = 0; n < 4; n++) {
      (monthObject.value.cohorts[0] !== null) ? show_months.push(monthObject.value.cohorts[0][n]) : '';
      (monthObject.value.cohorts[1] !== null) ? show_months.push(monthObject.value.cohorts[1][n]) : '';
    }
    for (let n = 4; n < 7; n++) {
      (monthObject.value.cohorts[0] !== null) ? hide_months.push(monthObject.value.cohorts[0][n]) : '';
      (monthObject.value.cohorts[1] !== null) ? hide_months.push(monthObject.value.cohorts[1][n]) : '';
    }
    hide_cohort_months(monthObject.value.cohorts[2])
  }

  show_cohort_months(show_months);
  hide_cohort_months(hide_months);
}

const hide_cohort_months = (months) => {
  if (months !== null){
    for(let m=0; m<months.length; m++) {
      if (months[m] !== null) {
        months[m].active = false;
      }
    }
  }
}

const show_cohort_months = (months) => {
  if(months !== null){
    for(let m=0; m<months.length; m++) {
      if (months[m] !== null) {
        months[m].active = true;
      }
    }
  }
}

const rotations_group_of_4 = computed(() => {
  return ([monthObject.value.rotations.slice(0,4), monthObject.value.rotations.slice(4,8)]);
});
</script>

<style lang="scss" scoped>
  div.month {
    min-height: 65px;
    font-size: 14px !important;
  }
  div.month:not(.round_8):hover{
    opacity: 0.8;
    background: lightgrey;
    cursor: pointer;
  }
  .columns{
    margin: 0px !important;
  }
  .column{
    width: 19.5px !important;
    padding: 2px !important;
    margin: 2px !important;
  }

  .dropdown_container{
    line-height: 10px;

    .select{
      font-size: 10px !important;
      height: 16px !important;
      width: 93px;
    }
  }
  .rotations_container{
    min-height: 58px;
  }
  .image_or_dropdown_container{
    min-height: 16px;
  }

  .image_container{
    background-size: contain;
    background-repeat: repeat-x;
    height: 16px;
    width: 86px;
    background: rgb(242, 242, 242);
    border-radius: 4px;
    margin-left: 2px !important;

    .image_icon{
      width: 56px;
    }
    .percentage_text{
      font-size: 12px;
      font-weight: bold;
      text-color: black;
      width: 25px !important;;
      padding-top: 0px !important;
      margin-right: 16px !important;
      margin-top: 0px !important;
    }
  }
  .round_1{
    background-color: #e1e2e3;

    .image_icon{
      background: url(../icons/icon-12-5-percent.svg) no-repeat, url(../icons/icon-0-percent.svg) repeat-x;
      background-size: 10px, 10px;
    }
  }
  .round_2{
    background-color: #c7c8ca;

    .image_icon{
      background: url(../icons/icon-25-percent.svg) no-repeat, url(../icons/icon-0-percent.svg) repeat-x;
      background-size: 10px, 10px;
    }
  }
  .round_3{
    background-color: #acaeb1;

    .image_icon{
      background: url(../icons/icon-25-percent.svg) no-repeat, url(../icons/icon-12-5-percent.svg) no-repeat, url(../icons/icon-0-percent.svg) repeat-x;
      background-size: 10px, 10px, 10px;
      background-position-x: 0px, 10px, 10px;
    }
  }
  .round_4{
    background-color: #939598;

    .image_icon{
      background: url(../icons/icon-25-percent.svg) no-repeat, url(../icons/icon-25-percent.svg) no-repeat, url(../icons/icon-0-percent.svg) repeat-x;
      background-size: 10px, 10px, 10px;
      background-position-x: 0px, 10px, 10px;
    }
  }
  .round_6{
    background-color: #636466;

    .image_icon{
      background: url(../icons/icon-25-percent.svg) no-repeat, url(../icons/icon-25-percent.svg) no-repeat, url(../icons/icon-12-5-percent.svg) no-repeat, url(../icons/icon-0-percent.svg) repeat-x;
      background-size: 10px, 10px, 10px, 10px;
      background-position-x: 0px, 10px, 20px, 30px;
    }
  }
  .round_8{
    background-color: rgb(50, 49, 51);
  }
  .round_8:hover .rotations_container, .round_8:hover .topics_container{
    opacity: 0.4;
  }
</style>